import React, { useState, useEffect } from 'react';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import PropTypes from 'prop-types';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { Timeline } from '../Timeline/Timeline';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import Dialog, { DialogAlignments, DialogTypes } from '../Dialog/Dialog';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { ModalOrderCard } from './subComponents/ModalOrderCard';
import { Loader } from '../common';

const SUCCESS_ICON = 'circleCheck';
const FAIL_ICON = 'warning';

const ModalConfirmedVoucherToGiftcard = ({
  order,
  isOpen,
  setIsOpen,
  token,
  setRequestDetails,
  onCancel,
}) => {
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const [showResponse, setShowResponse] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [responseContent, setResponseContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // trigger the giftcard tranformation - 42 hours required
  const changeActivityAction = async (order) => {
    try {
      setIsLoading(true);
      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        { order_id: order.id, type: 'voucher_to_giftcard' },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);

      setIsSuccess(true);
      setResponseContent(
        TRANSLATIONS.WE_ARE_PROCESSING_YOUR_REQUEST_IF_SUCCESSFUL_VOUCHER_WILL_BE_CONVERT_TO_GF
      );
    } catch (error) {
      setResponseContent(
        TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER
      );
    } finally {
      setShowResponse(true);
      setIsLoading(false);
    }
  };

  if (!order) return null;

  const timelineSteps = [
    {
      icon: 'message',
      status: 'approved',
      title: {
        text: TRANSLATIONS.SENDING_REQUEST,
        bold: true,
      },
    },
    {
      icon: 'spinner-dotted',
      status: 'approved',
      title: {
        text: TRANSLATIONS.PROCESSING,
        bold: true,
      },
    },
    {
      icon: 'document',
      status: 'pending',
      title: {
        text: TRANSLATIONS.OUTCOME,
        bold: true,
      },
    },
  ];

  const responseModalDrawer = (
    <ModalDrawer
      isOpen={showResponse}
      setIsOpen={setShowResponse}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={TRANSLATIONS.ACTIVITY_CHANGE_REQUEST}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100%">
          <div className="d-flex flex-column items-center pb-2xs gap-xs">
            <BetaIcon
              name={isSuccess ? SUCCESS_ICON : FAIL_ICON}
              color={IconColors.regular}
              size={IconSizes[64]}
            />
          </div>
          {isSuccess && (
            <div className="w-100% text-center pb-2xl">
              <div className="w-100% text-center pb-xl">
                <Typography
                  variant={TypographyVariants.p2}
                  weight={TypographyWeights.bold}
                >
                  {
                    TRANSLATIONS.CHANGE_ACTIVITY_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED
                  }
                </Typography>
              </div>
              <Timeline timelineSteps={timelineSteps} />
            </div>
          )}
          <div className="d-flex text-center align-center">
            <div
              className="typography__p2 typography__neutral text-center"
              dangerouslySetInnerHTML={{ __html: responseContent }}
            />
          </div>
        </div>
      }
      footer={
        <div className="d-flex justify-center gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={<b>{TRANSLATIONS.CLOSE}</b>}
            onClick={() => {
              setShowResponse(false);
              onCancel();
            }}
          />
        </div>
      }
    />
  );

  return (
    <>
      {/* generic result */}
      {responseModalDrawer}

      <ModalDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleAfterClose={() => {
          if (onCancel) onCancel();
        }}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
        modalCustomWidth={640}
        title={TRANSLATIONS.ACTIVITY_CHANGE_REQUEST}
        content={
          <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-xl">
            <div>
              <Typography
                classNames="text-left"
                variant={TypographyVariants.p2}
              >
                <b>{TRANSLATIONS.WANT_TO_REQUEST_AN_ACTIVITY_CHANGE}</b>
              </Typography>
              <Typography
                classNames="text-left"
                variant={TypographyVariants.p3}
              >
                {
                  TRANSLATIONS.IF_THE_VOUCHER_IS_ELIGIBLE_WILL_BE_CONVERT_TO_A_EQUAL_GF
                }
              </Typography>
            </div>
            <ModalOrderCard order={order} compact />
            <div>
              <Dialog
                align={DialogAlignments.row}
                type={DialogTypes.feature}
                content={TRANSLATIONS.CHANGE_VOUCHER_INSTRUCTIONS}
                icon={
                  <BetaIcon
                    name={'info'}
                    color={IconColors.regular}
                    size={IconSizes[64]}
                  />
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="d-flex gap-m pb-m pl-xl pr-xl">
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.empty}
              disabled={isLoading}
              label={TRANSLATIONS.CANCEL}
              onClick={() => {
                if (onCancel) onCancel();
                setIsOpen(false);
              }}
            />
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.secondary}
              disabled={isLoading}
              label={TRANSLATIONS.CONFIRM}
              onClick={() => {
                setIsOpen(false);
                changeActivityAction(order);
              }}
            />
          </div>
        }
      />
    </>
  );
};

ModalConfirmedVoucherToGiftcard.propTypes = {};

export { ModalConfirmedVoucherToGiftcard };
